/** Helpers shared by the database view models: people for person pickers, related databases for relations. */
import type { Database, FormulaPreviewRequest, FormulaPreviewResponse, PropertyDef } from "@bundle/contracts";
import { formulaDisplayText } from "../logic/formula.ts";
import { compareText, rowTitle, type DatabaseCache } from "../logic/database.ts";
import type { WorkspaceCache } from "../logic/workspace.ts";
import type { LiveQuery } from "../ports.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Someone a person property can hold. */
export type PersonOption = { userId: string; name: string; avatarUrl?: string; isAgent: boolean };

/** A page a relation property can point to. */
export type RelationOption = { id: string; title: string; icon?: string };

/** Active members (people and agents) by name. */
export function peopleOf(cache: WorkspaceCache | undefined): PersonOption[] {
  const people: PersonOption[] = [];
  for (const m of Object.values(cache?.members ?? {})) {
    if (m.deactivated) continue;
    const p: PersonOption = { userId: m.userId, name: m.displayName, isAgent: m.kind === "agent" };
    if (m.avatarUrl !== undefined) p.avatarUrl = m.avatarUrl;
    people.push(p);
  }
  return people.toSorted((a, b) => compareText(a.name.toLowerCase(), b.name.toLowerCase()) || compareText(a.userId, b.userId));
}

/** Member names by id (sorting and grouping by person). */
export function namesOf(cache: WorkspaceCache | undefined): Record<string, string> {
  const names: Record<string, string> = {};
  for (const [id, m] of Object.entries(cache?.members ?? {})) names[id] = m.displayName;
  return names;
}

/** Live caches of the databases a database's relation properties point to. */
export class RelatedDatabases {
  private readonly ws: WorkspaceViewModel;
  private readonly onChange: () => void;
  private readonly queries = new Map<string, LiveQuery<DatabaseCache>>();
  private readonly subs = new Map<string, () => void>();

  constructor(ws: WorkspaceViewModel, onChange: () => void) {
    this.ws = ws;
    this.onChange = onChange;
  }

  /**
   * Follows the relation targets of `db` (its own rows are passed to the selectors directly) and `also` (the other
   * databases its Sheet formulas read).
   */
  sync(db: Database, also: string[] = []): void {
    const targets = new Set<string>(also.filter((id) => id !== db.id));
    for (const p of db.properties) if (p.type === "relation" && p.relation !== undefined && p.relation.databaseId !== db.id) targets.add(p.relation.databaseId);
    for (const [id, q] of this.queries) {
      if (targets.has(id)) continue;
      this.subs.get(id)?.();
      this.subs.delete(id);
      q.dispose();
      this.queries.delete(id);
    }
    for (const id of targets) {
      if (this.queries.has(id)) continue;
      const q = this.ws.deps.data.database(this.ws.wsId, id);
      this.queries.set(id, q);
      this.subs.set(id, q.subscribe(() => this.onChange()));
    }
  }

  dispose(): void {
    for (const unsub of this.subs.values()) unsub();
    for (const q of this.queries.values()) q.dispose();
    this.subs.clear();
    this.queries.clear();
  }

  /** A followed database's cache (once loaded). */
  cacheOf(databaseId: string): DatabaseCache | undefined {
    return this.queries.get(databaseId)?.getSnapshot().data;
  }

  private target(own: DatabaseCache, databaseId: string): DatabaseCache | undefined {
    return own.database.id === databaseId ? own : this.queries.get(databaseId)?.getSnapshot().data;
  }

  /** Relation property id → candidate pages (rank order). */
  options(own: DatabaseCache): Record<string, RelationOption[]> {
    const out: Record<string, RelationOption[]> = {};
    for (const p of own.database.properties) {
      if (p.type !== "relation" || p.relation === undefined) continue;
      const t = this.target(own, p.relation.databaseId);
      const list: RelationOption[] = [];
      if (t !== undefined) {
        for (const r of t.rows) {
          const o: RelationOption = { id: r.id, title: rowTitle(t.database, r) };
          if (r.icon !== undefined) o.icon = r.icon;
          list.push(o);
        }
      }
      out[p.id] = list;
    }
    return out;
  }

  /** Relation property id → the properties of the database it points to (rollup settings). */
  targetProperties(own: DatabaseCache): Record<string, PropertyDef[]> {
    const out: Record<string, PropertyDef[]> = {};
    for (const p of own.database.properties) {
      if (p.type !== "relation" || p.relation === undefined) continue;
      out[p.id] = this.target(own, p.relation.databaseId)?.database.properties ?? [];
    }
    return out;
  }

  /** Page id → title for every known row (own and related databases). */
  titles(own: DatabaseCache): Record<string, string> {
    const out: Record<string, string> = {};
    for (const r of own.rows) out[r.id] = rowTitle(own.database, r);
    for (const q of this.queries.values()) {
      const c = q.getSnapshot().data;
      if (c !== undefined) for (const r of c.rows) out[r.id] = rowTitle(c.database, r);
    }
    return out;
  }
}

/** A stored formula as the user types it (core `formulaDisplayText` over the database's rows); "" while loading. */
export function formulaText(cache: DatabaseCache | undefined, expression: string): string {
  return cache === undefined ? expression : formulaDisplayText(expression, cache.database, cache.rows);
}

/** Runs a formula preview on the server (undefined offline or on failure: the editor then shows no preview). */
export async function previewFormula(ws: WorkspaceViewModel, databaseId: string, req: FormulaPreviewRequest): Promise<FormulaPreviewResponse | undefined> {
  try {
    return await ws.deps.api.previewFormula(ws.wsId, databaseId, req);
  } catch {
    return undefined;
  }
}
